// Offline map tile cache backed by IndexedDB (via localforage).
// Works in web, Android WebView and iOS WKWebView (service workers are NOT
// available in iOS Capacitor, so we cache tiles ourselves).
import localforage from 'localforage';

const tileStore = localforage.createInstance({
  name: 'boat360',
  storeName: 'map_tiles',
  description: 'Cached map tiles for offline navigation',
});

const metaStore = localforage.createInstance({
  name: 'boat360',
  storeName: 'map_tiles_meta',
});

const MANIFEST_KEY = 'manifest';
const MAX_BYTES = 180 * 1024 * 1024; // ~180 MB cap

// In-memory manifest: array of { k: key, s: sizeBytes } in insertion (LRU) order.
let manifest = [];
let manifestBytes = 0;
let loaded = false;
let saveTimer = null;

async function ensureLoaded() {
  if (loaded) return;
  try {
    const saved = await metaStore.getItem(MANIFEST_KEY);
    if (Array.isArray(saved)) {
      manifest = saved;
      manifestBytes = manifest.reduce((a, e) => a + (e.s || 0), 0);
    }
  } catch (e) {
    manifest = [];
    manifestBytes = 0;
  }
  loaded = true;
}

function scheduleSave() {
  if (saveTimer) return;
  saveTimer = setTimeout(async () => {
    saveTimer = null;
    try {
      await metaStore.setItem(MANIFEST_KEY, manifest);
    } catch (e) {
      /* ignore */
    }
  }, 4000);
}

export async function getTile(key) {
  await ensureLoaded();
  try {
    return await tileStore.getItem(key);
  } catch (e) {
    return null;
  }
}

export async function putTile(key, blob) {
  await ensureLoaded();
  const size = blob && blob.size ? blob.size : 0;
  if (!size) return;
  try {
    await tileStore.setItem(key, blob);
  } catch (e) {
    return; // quota or storage error - skip caching
  }
  // Track in manifest (dedupe existing)
  const existingIdx = manifest.findIndex((e) => e.k === key);
  if (existingIdx !== -1) {
    manifestBytes -= manifest[existingIdx].s || 0;
    manifest.splice(existingIdx, 1);
  }
  manifest.push({ k: key, s: size });
  manifestBytes += size;

  // Evict oldest until under cap
  while (manifestBytes > MAX_BYTES && manifest.length > 0) {
    const oldest = manifest.shift();
    manifestBytes -= oldest.s || 0;
    tileStore.removeItem(oldest.k).catch(() => {});
  }
  scheduleSave();
}

export async function clearTileCache() {
  await ensureLoaded();
  manifest = [];
  manifestBytes = 0;
  try {
    await tileStore.clear();
    await metaStore.removeItem(MANIFEST_KEY);
  } catch (e) {
    /* ignore */
  }
}

export async function getCacheStats() {
  await ensureLoaded();
  return { count: manifest.length, bytes: manifestBytes };
}

export function formatBytes(bytes) {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;
  return `${mb.toFixed(1)} MB`;
}
